import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { PageWithNav } from "components/PageWithNav";
import { Spinner } from "components/Spinner";
import { TextEditor } from "components/TextEditor";
import api from "utils/api";
import { useToastContext } from "context/ToastContext";

export const ManageCourse = () => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { notify } = useToastContext();
  const [heroImage, setHeroImage] = useState(null);
  const [description, setDescription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [data, setData] = useState({});
  const [hasError, setHasError] = useState(false);

  const loadCourseData = useCallback(async () => {
    try {
      const response = await api.get(`/course/${courseId}`);
      const courseData = response.data.course;
      setData(courseData);
      setDescription(courseData.description);
      setHeroImage(courseData.hero_image);
    } catch {
      setHasError(true);
    } finally {
      setLoadingData(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadCourseData();
  }, [loadCourseData]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    formData.set("description", description);
    formData.set("hero_image", heroImage);

    try {
      setIsLoading(true);
      await api.post(`/course/${courseId}`, formData);
      setIsLoading(false);
      notify({ title: "Success", description: "Course updated!", type: "success" });
      navigate("/courses");
    } catch (e) {
      setIsLoading(false);
      console.error(e);
    }
  };

  return (
    <PageWithNav>
      <h1 className="mb-5 pe-4 text-xl font-semibold text-neutral-900">Update course</h1>
      {loadingData && (
        <div className="flex justify-center">
          <Spinner size="lg" color="primary" />
        </div>
      )}
      {hasError && !isLoading && <div className="flex justify-center text-xl font-semibold leading-3 text-red-900">There was a problem, refresh the page to try again.</div>}
      {!hasError && !loadingData && (
        <form onSubmit={handleSubmit} data-testid="update-course">
          <Input id="name" defaultValue={data.name} name="name" type="text" required label="Course name" placeholder="f.e Detailing" />
          <Input id="time_to_complete" defaultValue={data.time_to_complete} name="time_to_complete" type="text" required label="Time to complete" placeholder="15 hours" />
          <Input id="hero_image" value={heroImage} onChange={(e) => setHeroImage(e.target.value)} name="hero_image" type="text" required label="Hero Image" placeholder="https://gcs.image.png" />
          {heroImage && (
            <div className="max-w-100 mx-auto my-4 items-center rounded-lg bg-gray-100 p-6 text-center">
              <img src={heroImage} alt="Preview" className="mx-auto max-h-48 rounded-lg" />
            </div>
          )}
          <TextEditor value={description} onChange={setDescription} id="description" label="Course description (more detailed course info)" />
          <div className="my-4 flex gap-2">
            <Button disabled={isLoading} onClick={() => navigate("/courses")} color="secondary" size="sm">
              Cancel
            </Button>
            <Button disabled={isLoading} loading={isLoading} type="submit" color="primary" size="sm">
              Save
            </Button>
          </div>
        </form>
      )}
    </PageWithNav>
  );
};
